'use client';

import React from 'react';
import Link from 'next/link';
import Icon from '@/components/ui/AppIcon';

const CookiePolicyPage = () => {
    const lastUpdated = 'May 4, 2026';

    return (
        <main className="min-h-screen bg-white pt-32 pb-20 lg:pt-40 lg:pb-32">
            <div className="max-w-4xl mx-auto px-6 lg:px-8">

                {/* Header */}
                <div className="mb-16">
                    <Link href="/" className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-indigo-600 transition-colors mb-8 group">
                        <Icon name="ArrowLeftIcon" className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
                        Back to Home
                    </Link>
                    <h1 className="text-4xl lg:text-5xl font-black text-slate-900 tracking-tight mb-4">
                        Cookie <span className="text-indigo-600">Policy</span>
                    </h1>
                    <p className="text-slate-500 text-sm font-medium">
                        Last Updated: {lastUpdated}
                    </p>
                </div>

                {/* Content */}
                <div className="prose prose-slate prose-lg max-w-none space-y-10">

                    <section>
                        <p className="text-slate-600 leading-relaxed text-base">
                            This Cookie Policy explains how Site Map Genius (&quot;we,&quot; &quot;our,&quot; or &quot;us&quot;), a sole proprietorship operated by Abdul Sohail Samad, uses cookies and similar technologies on our website <strong>sitemapgenius.com</strong> (the &quot;Website&quot;).
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                            <span className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 text-sm font-bold">1</span>
                            What Are Cookies?
                        </h2>
                        <p className="text-slate-600 leading-relaxed text-base">
                            Cookies are small text files that are placed on your device (computer, tablet, or mobile phone) when you visit a website. They are widely used to make websites work efficiently and to provide information to website owners. Cookies help improve your browsing experience by remembering your preferences and enabling certain features.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                            <span className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 text-sm font-bold">2</span>
                            Cookies We Use
                        </h2>
                        <p className="text-slate-600 leading-relaxed text-base mb-4">
                            We maintain a minimal cookie footprint. Our Website uses <strong>only essential cookies</strong> that are strictly necessary for the website to function properly:
                        </p>

                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse text-sm">
                                <thead>
                                    <tr className="bg-slate-50 border-b border-slate-200">
                                        <th className="text-left p-4 font-bold text-slate-900">Cookie Type</th>
                                        <th className="text-left p-4 font-bold text-slate-900">Purpose</th>
                                        <th className="text-left p-4 font-bold text-slate-900">Duration</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr className="border-b border-slate-100">
                                        <td className="p-4 text-slate-700 font-medium">Session Cookies</td>
                                        <td className="p-4 text-slate-600">Required for basic website functionality, maintaining session state during your visit, and enabling form submissions.</td>
                                        <td className="p-4 text-slate-600">Expires when you close your browser</td>
                                    </tr>
                                    <tr className="border-b border-slate-100">
                                        <td className="p-4 text-slate-700 font-medium">CSRF Token</td>
                                        <td className="p-4 text-slate-600">Protects against cross-site request forgery attacks when you submit forms (contact/consultation).</td>
                                        <td className="p-4 text-slate-600">Session-based</td>
                                    </tr>
                                </tbody>
                            </table>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                            <span className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 text-sm font-bold">3</span>
                            Cookies We Do NOT Use
                        </h2>
                        <p className="text-slate-600 leading-relaxed text-base mb-4">
                            We want to be transparent about what we do <strong>not</strong> use. The following technologies are <strong>not present</strong> on our Website:
                        </p>
                        <ul className="space-y-3 text-slate-600 text-base">
                            <li className="flex items-start gap-3 p-3 rounded-xl bg-red-50/50 border border-red-100/50">
                                <Icon name="XCircleIcon" className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                                <div><strong>Google Analytics</strong> — We do not track your browsing behaviour or page views.</div>
                            </li>
                            <li className="flex items-start gap-3 p-3 rounded-xl bg-red-50/50 border border-red-100/50">
                                <Icon name="XCircleIcon" className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                                <div><strong>Facebook/Meta Pixel</strong> — We do not track conversions or retarget you with ads.</div>
                            </li>
                            <li className="flex items-start gap-3 p-3 rounded-xl bg-red-50/50 border border-red-100/50">
                                <Icon name="XCircleIcon" className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                                <div><strong>Advertising Cookies</strong> — We do not serve targeted or personalised advertisements.</div>
                            </li>
                            <li className="flex items-start gap-3 p-3 rounded-xl bg-red-50/50 border border-red-100/50">
                                <Icon name="XCircleIcon" className="w-5 h-5 text-red-400 mt-0.5 flex-shrink-0" />
                                <div><strong>Third-Party Tracking</strong> — We do not share browsing data with any third-party services for marketing or profiling purposes.</div>
                            </li>
                        </ul>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                            <span className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 text-sm font-bold">4</span>
                            Third-Party Embedded Content
                        </h2>
                        <p className="text-slate-600 leading-relaxed text-base">
                            Our Website may embed or link to third-party services such as Google Maps, WhatsApp, and Instagram. When you interact with these services (e.g., clicking a WhatsApp link or viewing a map), those third parties may set their own cookies in accordance with their own privacy and cookie policies. We have no control over these cookies and recommend reviewing the respective policies of those services.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                            <span className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 text-sm font-bold">5</span>
                            Managing Cookies
                        </h2>
                        <p className="text-slate-600 leading-relaxed text-base mb-4">
                            You can control and manage cookies through your browser settings. Most browsers allow you to:
                        </p>
                        <ul className="space-y-2 text-slate-600 text-base">
                            <li className="flex items-start gap-2"><Icon name="ArrowRightIcon" className="w-4 h-4 text-indigo-600 mt-1 flex-shrink-0" /> View what cookies are stored on your device.</li>
                            <li className="flex items-start gap-2"><Icon name="ArrowRightIcon" className="w-4 h-4 text-indigo-600 mt-1 flex-shrink-0" /> Delete cookies individually or in bulk.</li>
                            <li className="flex items-start gap-2"><Icon name="ArrowRightIcon" className="w-4 h-4 text-indigo-600 mt-1 flex-shrink-0" /> Block cookies from specific or all websites.</li>
                            <li className="flex items-start gap-2"><Icon name="ArrowRightIcon" className="w-4 h-4 text-indigo-600 mt-1 flex-shrink-0" /> Set your browser to notify you when a cookie is being set.</li>
                        </ul>
                        <p className="text-slate-600 leading-relaxed text-base mt-4">
                            <strong>Note:</strong> Disabling essential cookies may affect the functionality of certain features on our Website, such as form submissions.
                        </p>
                        <div className="mt-4 p-5 bg-slate-50 rounded-2xl border border-slate-100">
                            <h4 className="font-bold text-slate-900 text-sm mb-3">Browser Cookie Settings:</h4>
                            <ul className="space-y-2 text-sm text-slate-600">
                                <li className="flex items-center gap-2">
                                    <span className="font-medium text-slate-700">Chrome:</span>
                                    Settings → Privacy and Security → Cookies
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="font-medium text-slate-700">Firefox:</span>
                                    Settings → Privacy & Security → Cookies
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="font-medium text-slate-700">Safari:</span>
                                    Preferences → Privacy → Manage Website Data
                                </li>
                                <li className="flex items-center gap-2">
                                    <span className="font-medium text-slate-700">Edge:</span>
                                    Settings → Cookies and Site Permissions
                                </li>
                            </ul>
                        </div>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                            <span className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 text-sm font-bold">6</span>
                            Changes to This Policy
                        </h2>
                        <p className="text-slate-600 leading-relaxed text-base">
                            We reserve the right to update this Cookie Policy at any time without prior notice. If we begin using additional cookies or tracking technologies in the future, this policy will be updated accordingly. Any changes will be effective immediately upon posting with an updated &quot;Last Updated&quot; date.
                        </p>
                    </section>

                    <section>
                        <h2 className="text-2xl font-bold text-slate-900 mb-4 flex items-center gap-3">
                            <span className="w-8 h-8 rounded-lg bg-indigo-50 flex items-center justify-center text-indigo-600 text-sm font-bold">7</span>
                            Contact Us
                        </h2>
                        <p className="text-slate-600 leading-relaxed text-base">
                            If you have any questions about our use of cookies, please contact us:
                        </p>
                        <div className="mt-4 p-6 bg-slate-50 rounded-2xl border border-slate-100 space-y-3">
                            <p className="text-slate-700 font-semibold text-base">Site Map Genius</p>
                            <p className="text-slate-600 text-sm">Proprietor: Abdul Sohail Samad</p>
                            <p className="text-slate-600 text-sm flex items-center gap-2"><Icon name="EnvelopeIcon" className="w-4 h-4 text-indigo-600" /> sitemapgenius@gmail.com</p>
                            <p className="text-slate-600 text-sm flex items-center gap-2"><Icon name="PhoneIcon" className="w-4 h-4 text-indigo-600" /> +91 8686284396</p>
                            <p className="text-slate-600 text-sm flex items-center gap-2"><Icon name="MapPinIcon" className="w-4 h-4 text-indigo-600" /> Hyderabad, Telangana, India</p>
                        </div>
                    </section>

                    {/* Footer Note */}
                    <section className="pt-8 border-t border-slate-200">
                        <p className="text-sm text-slate-400 leading-relaxed">
                            This Cookie Policy should be read alongside our <Link href="/privacy" className="text-indigo-600 hover:text-indigo-800 transition-colors">Privacy Policy</Link> and <Link href="/terms" className="text-indigo-600 hover:text-indigo-800 transition-colors">Terms of Service</Link>. This policy is governed by the laws of India, with exclusive jurisdiction in Hyderabad, Telangana.
                        </p>
                    </section>

                </div>
            </div>
        </main>
    );
};

export default CookiePolicyPage;
